import {SizeSelection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  BORDER,
  COLORS,
  GenericIconName,
  Icon,
  RichText,
  SPACING,
  Text,
  UnstyledButton,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useMemo} from 'react';
import {useRecommendedSize} from '../../../../features/SizeSelection/hooks/useRecommendedSize';

export const SizeAdvisorButton: React.FC<{
  onClick: VoidFunction;
  sizeSelection: SizeSelection;
  className?: string;
  productId?: number;
  shouldUseMySizeFinderButton?: boolean;
}> = ({
  className,
  onClick,
  productId,
  sizeSelection,
  shouldUseMySizeFinderButton,
}) => {
  const {t} = useTranslation();
  const recommendedSize = useRecommendedSize({
    sizeSelection,
    productId,
  })?.label;

  const uncertainAboutSizeText = t('Unsure about the size?');
  const defaultText = t('Use the <u>Size Finder →</u>');
  const specialText = t('Your recommended size: {recommendedSize}', {
    recommendedSize,
  });

  const textComponent = useMemo(() => {
    if (recommendedSize) {
      return (
        <SizeAdvisorTextRecommended
          size="px12"
          weight="mediumBold"
          color="Black"
        >
          {specialText}
        </SizeAdvisorTextRecommended>
      );
    }

    if (shouldUseMySizeFinderButton) {
      return (
        <MySizeFinderTextWrapper data-testid="mySizeFinderText">
          <MySizeFinderText size="px12" color="Black" weight="mediumHeavy">
            {t('My Size Finder')}
          </MySizeFinderText>
          <Icon asset={GenericIconName.DropdownUpRight} size="XS" />
        </MySizeFinderTextWrapper>
      );
    }

    return (
      <>
        <Text
          data-testid="uncertainAboutSizeText"
          size="px12"
          weight="mediumBold"
          color="Text.Grey"
        >
          {uncertainAboutSizeText}
        </Text>
        <SizeAdvisorText
          data-testid="sizeAdvisorText"
          size="px12"
          weight="mediumBold"
          color="Black"
        >
          {defaultText}
        </SizeAdvisorText>
      </>
    );
  }, [
    recommendedSize,
    shouldUseMySizeFinderButton,
    uncertainAboutSizeText,
    defaultText,
    specialText,
  ]);

  return (
    <StyledButton
      data-testid={'sizeAdvisorWrapper'}
      onClick={onClick}
      className={className}
      $addMarginLeft={!shouldUseMySizeFinderButton}
      aria-label={undefined}
    >
      {textComponent}
    </StyledButton>
  );
};

const MySizeFinderTextWrapper = styled.div`
  display: flex;
  padding-top: 6px;
`;

const MySizeFinderText = styled(Text)`
  border-bottom: ${BORDER.Sm} solid ${COLORS.Grey1};
  padding-bottom: ${SPACING['Y.Md']};
`;

const SizeAdvisorText = styled(RichText)`
  u {
    text-decoration: none;
    padding-bottom: 3px;
    // eslint-disable-next-line local-rules/css-layout-animation
    transition: border-bottom 0.2s ease-in-out;
    border-bottom: ${BORDER.Sm} solid ${COLORS.Grey1};
  }
`;

const SizeAdvisorTextRecommended = styled(Text)`
  padding-bottom: 3px;
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: border-bottom 0.2s ease-in-out;
  border-bottom: ${BORDER.Sm} solid ${COLORS.Grey1};
`;

const StyledButton = styled(UnstyledButton)<{$addMarginLeft: boolean}>`
  all: unset;
  column-gap: ${SPACING['X.Sm']};
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  justify-content: end;
  margin-left: ${({$addMarginLeft}) => ($addMarginLeft ? '0.5rem' : 0)};
  text-align: right;

  :hover {
    ${SizeAdvisorText} u,
    ${SizeAdvisorTextRecommended} {
      border-bottom: ${BORDER.Sm} solid transparent;
    }
  }

  &:focus-visible {
    outline: 2px auto Highlight;
    outline: 2px auto -webkit-focus-ring-color;
  }
`;
